import Honeybadger from 'honeybadger-js';
import React, { useMemo } from 'react';

import { useSendLivestreamEmailMutation } from '../../../generated/graphql';
import { getChangedValues } from '../../../helpers/getChangedValues';
import { splitHouseholds } from '../../../helpers/households';
import { editLivestreamEmailPath, editLivestreamEmailRoute } from '../../../helpers/routes';
import EmailSequence from '../../SharePage/EmailSequence';
import { emailSharingSchema } from '../../SharePage/sharingValidation';
import useShareTracking from '../../SharePage/useShareTracking';
import { EmailPreviewContainer } from '../EmailPreviewContainer';
import { useFilteredHouseholds } from '../GuestFilters/useFilteredHouseholds';
import EmailEditorRail from './ShareWebsiteEmail/EmailEditorRail';
import { useSequenceState } from './useSequenceState';

export interface LivestreamValues {
  subject: string;
  message: string;
  customUrl: string;
  linkButtonText: string;
}

const LivestreamEmail: React.FC = () => {
  const [sendEmail, { loading: sendingMessage }] = useSendLivestreamEmailMutation();

  const {
    sequence,
    wwsLoading,
    guestIds,
    setGuestIds,
    setHideRecaptcha,
    hideRecaptcha,
    dispatch,
    recaptchaRef,
    seqId,
    showError,
    errorMessage,
    showSuccess,
    householdLoading,
  } = useSequenceState();

  const { filteredHouseholds, setFilterCriteria } = useFilteredHouseholds();
  const households = useMemo(() => splitHouseholds('email', filteredHouseholds), [filteredHouseholds]);
  const loading = wwsLoading || sendingMessage;
  const track = useShareTracking();

  const message = sequence?.messagingSequence?.defaultEmailBody ?? '';

  let initialValues: LivestreamValues = {
    subject: sequence?.messagingSequence?.defaultEmailSubject ?? '',
    message,
    linkButtonText: sequence?.messagingSequence?.defaultLinkText ?? '',
    customUrl: 'Enter your stream link here',
  };

  const sendMessage = async (values: LivestreamValues) => {
    if (loading) {
      return;
    }

    const edited = getChangedValues(initialValues, values);

    track('Website Shared', {
      guestMessagedCount: guestIds.length,
      message: values.message,
      messageCustomized: Boolean(Object.keys(edited).length),
      messageType: sequence?.messagingSequence?.displayName,
      medium: 'email',
    });

    dispatch({ type: 'RESET', payload: { hideRecaptcha: true, showSuccess: false, showError: false } });

    try {
      // Convert all newlines in the message body into <br /> tags.
      const convertedMessage = values.message.replace(/\n/g, '<br/>');
      await sendEmail({
        variables: {
          args: {
            subject: values.subject,
            body: convertedMessage,
            linkButtonText: values.linkButtonText,
            customUrl: values.customUrl,
            guestIds: guestIds,
            token: recaptchaRef.current?.getValue() || '',
          },
        },
      });
      dispatch({ type: 'SUCCESS' });
    } catch (error) {
      Honeybadger.notify(error, 'Error sending livestream info');
      dispatch({ type: 'ERROR', payload: { message: error.message } });
    } finally {
      recaptchaRef.current?.reset();
    }
  };

  const editRoute = editLivestreamEmailRoute(seqId);

  return (
    <EmailSequence
      loading={loading}
      showError={showError}
      errorMessage={errorMessage}
      showSuccess={showSuccess}
      initialValues={initialValues}
      onSend={sendMessage}
      PreviewComponent={
        <EmailPreviewContainer
          editRoute={editRoute}
          matchPath={editLivestreamEmailPath}
          displayName={sequence?.messagingSequence?.displayName}
        />
      }
      EditorComponent={
        <EmailEditorRail matchPath={editLivestreamEmailPath} customUrl={true} initialValues={initialValues} />
      }
      hideRecaptcha={hideRecaptcha}
      setHideRecaptcha={setHideRecaptcha}
      recaptchaRef={recaptchaRef}
      setGuestIds={setGuestIds}
      editPath={editLivestreamEmailPath}
      editRoute={editRoute}
      validationSchema={emailSharingSchema}
      households={households}
      householdLoading={householdLoading}
      setFilter={setFilterCriteria}
      displayName={sequence?.messagingSequence?.displayName}
    />
  );
};

export default LivestreamEmail;
